/**
 * ZoneRenderer - Renders content within a single zone
 * Each zone can contain either a playlist or a design
 */

import { manifestLoader } from './manifest-loader';
import type {
  ManifestZone,
  ManifestPlaylist,
  ManifestAsset,
  ManifestDesign,
  ManifestFont,
  DesignLayer,
  PlayEvent,
  ZoneTextContent,
} from '../types';
import { PLAYER_CONFIG } from '../config';
import { ClockWidget, WeatherWidget, QRCodeWidget, RSSWidget, type Widget } from './widgets';

type PlayEventCallback = (event: PlayEvent) => void;

export class ZoneRenderer {
  private zone: ManifestZone;
  private container: HTMLElement;
  private contentContainer: HTMLElement;
  private currentElement: HTMLElement | null = null;
  private playlist: ManifestPlaylist | null = null;
  private assets: Map<string, ManifestAsset> = new Map();
  private currentItemIndex = 0;
  private isPlaying = false;
  private playTimer: ReturnType<typeof setTimeout> | null = null;
  private currentAssetId: string | null = null;
  private playStartTime = 0;
  private onPlayEvent: PlayEventCallback | null = null;
  private activeWidgets: Widget[] = []; // Track widgets for cleanup
  private abortController: AbortController | null = null; // For cancelling waits
  private styleElements: HTMLStyleElement[] = []; // Track style elements for cleanup

  constructor(zone: ManifestZone, parentContainer: HTMLElement) {
    this.zone = zone;

    // Create zone container
    this.container = document.createElement('div');
    this.container.id = `zone-${zone.id}`;
    this.container.className = 'zone-container';
    this.container.style.cssText = `
      position: absolute;
      left: ${zone.x}%;
      top: ${zone.y}%;
      width: ${zone.width}%;
      height: ${zone.height}%;
      z-index: ${zone.zIndex};
      overflow: hidden;
      ${zone.backgroundColor ? `background-color: ${zone.backgroundColor};` : 'background-color: #000;'}
      ${zone.borderRadius ? `border-radius: ${zone.borderRadius}px;` : ''}
    `;

    // Create content container inside zone
    this.contentContainer = document.createElement('div');
    this.contentContainer.className = 'zone-content';
    this.contentContainer.style.cssText = `
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    `;

    this.container.appendChild(this.contentContainer);
    parentContainer.appendChild(this.container);
  }

  setPlayEventCallback(callback: PlayEventCallback): void {
    this.onPlayEvent = callback;
  }

  /**
   * Start playing a playlist in this zone
   */
  startPlaylist(playlist: ManifestPlaylist, assets: ManifestAsset[]): void {
    // Stop any existing playback first to prevent race conditions
    this.stopPlayback();

    this.playlist = playlist;
    this.assets = new Map(assets.map((a) => [a.id, a]));
    this.currentItemIndex = 0;
    this.isPlaying = true;

    // Create new abort controller for this playback session
    this.abortController = new AbortController();

    // Start playback and handle any errors
    this.playNextItem().catch((error) => {
      if (error?.name !== 'AbortError') {
        console.error(`Zone ${this.zone.name}: Playback error:`, error);
      }
    });
  }

  /**
   * Stop current playback cleanly (abort waits and clear timers)
   */
  private stopPlayback(): void {
    this.isPlaying = false;

    // Abort any pending waits
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Clear any pending timers
    if (this.playTimer) {
      clearTimeout(this.playTimer);
      this.playTimer = null;
    }
  }

  /**
   * Render a design in this zone
   */
  renderDesign(design: ManifestDesign): void {
    this.isPlaying = false;
    this.renderDesignLayers(design);
  }

  /**
   * Render a single asset in this zone
   */
  async renderSingleAsset(
    asset: ManifestAsset,
    fitMode: 'FIT' | 'FILL' | 'contain' | 'cover' | 'fill' | 'none' = 'FIT'
  ): Promise<void> {
    this.isPlaying = false;
    this.destroyWidgets();
    this.contentContainer.innerHTML = '';

    const objectFit = this.mapFitMode(fitMode);
    const url = (await manifestLoader.getAssetUrl(asset.id)) || asset.url;

    let element: HTMLElement;

    if (asset.type === 'video') {
      const video = document.createElement('video');
      video.src = url;
      video.autoplay = true;
      video.loop = true; // Single asset loops indefinitely
      video.muted = true;
      video.playsInline = true;
      video.style.cssText = `
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: ${objectFit};
      `;
      element = video;
    } else {
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.draggable = false;
      img.style.cssText = `
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: ${objectFit};
      `;
      element = img;
    }

    this.contentContainer.appendChild(element);
    this.currentElement = element;
  }

  /**
   * Render text content in this zone
   */
  renderText(textContent: ZoneTextContent): void {
    console.log(`[ZoneRenderer] Rendering text in zone "${this.zone.name}":`, {
      content: textContent.content,
      fontSize: textContent.fontSize,
      color: textContent.color,
      backgroundColor: textContent.backgroundColor,
      scrolling: textContent.scrolling,
      alignment: textContent.alignment,
    });

    this.isPlaying = false;
    this.destroyWidgets();
    this.contentContainer.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: ${textContent.alignment === 'center' ? 'center' : textContent.alignment === 'right' ? 'flex-end' : 'flex-start'};
      background-color: ${textContent.backgroundColor || 'transparent'};
      overflow: hidden;
      padding: 10px;
      box-sizing: border-box;
    `;

    const textElement = document.createElement('div');
    textElement.textContent = textContent.content;
    textElement.style.cssText = `
      font-size: ${textContent.fontSize || 24}px;
      font-weight: ${textContent.fontWeight || 'normal'};
      font-style: ${textContent.fontStyle || 'normal'};
      color: ${textContent.color || '#ffffff'};
      text-align: ${textContent.alignment || 'center'};
      white-space: ${textContent.scrolling ? 'nowrap' : 'pre-wrap'};
      ${textContent.scrolling ? `
        animation: marquee ${textContent.scrollSpeed || 10}s linear infinite;
      ` : ''}
    `;

    // Add marquee animation if scrolling is enabled
    if (textContent.scrolling) {
      const style = document.createElement('style');
      style.textContent = `
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `;
      wrapper.appendChild(style);
      this.styleElements.push(style); // Track for cleanup
    }

    wrapper.appendChild(textElement);
    this.contentContainer.appendChild(wrapper);
    this.currentElement = wrapper;
  }

  /**
   * Render a widget in this zone
   */
  renderWidget(
    widgetType: 'clock' | 'weather' | 'ticker' | 'rss',
    config: Record<string, unknown> = {}
  ): void {
    this.isPlaying = false;
    this.destroyWidgets();
    this.contentContainer.innerHTML = '';

    const container = document.createElement('div');
    container.className = `widget widget-${widgetType}`;
    container.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    let widget: Widget | null = null;

    switch (widgetType) {
      case 'clock':
        widget = new ClockWidget(config);
        break;

      case 'weather':
        widget = new WeatherWidget(config);
        break;

      case 'ticker':
        // For zone-level ticker, render as scrolling text
        container.innerHTML = `
          <div style="width: 100%; overflow: hidden;">
            <div style="white-space: nowrap; animation: marquee 15s linear infinite;">
              ${(config.text as string) || 'Breaking News: Configure ticker text in settings...'}
            </div>
          </div>
          <style>
            @keyframes marquee {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          </style>
        `;
        this.contentContainer.appendChild(container);
        this.currentElement = container;
        return;

      case 'rss':
        widget = new RSSWidget(config);
        break;

      default:
        console.warn(`[ZoneRenderer] Unknown widget type: ${widgetType}`);
        container.innerHTML = `<div style="opacity: 0.5;">${widgetType} Widget</div>`;
        this.contentContainer.appendChild(container);
        this.currentElement = container;
        return;
    }

    if (widget) {
      const widgetElement = widget.render();
      container.appendChild(widgetElement);
      this.activeWidgets.push(widget);
    }

    this.contentContainer.appendChild(container);
    this.currentElement = container;
  }

  /**
   * Map frontend fit mode to CSS object-fit
   */
  private mapFitMode(fitMode: 'FIT' | 'FILL' | 'contain' | 'cover' | 'fill' | 'none'): string {
    switch (fitMode) {
      case 'FIT':
      case 'contain':
        return 'contain';
      case 'FILL':
      case 'cover':
        return 'cover';
      case 'fill':
        return 'fill';
      case 'none':
        return 'none';
      default:
        return 'contain';
    }
  }

  /**
   * Render design layers
   */
  private renderDesignLayers(design: ManifestDesign): void {
    // Destroy existing widgets before clearing content
    this.destroyWidgets();

    // Clear existing content
    this.contentContainer.innerHTML = '';

    // Set background
    if (design.background) {
      if (design.background.type === 'color' || design.background.type === 'solid') {
        this.contentContainer.style.backgroundColor = design.background.value;
      } else if (design.background.type === 'gradient') {
        this.contentContainer.style.background = design.background.value;
      } else if (design.background.type === 'image') {
        this.contentContainer.style.backgroundImage = `url(${design.background.value})`;
        this.contentContainer.style.backgroundSize = 'cover';
        this.contentContainer.style.backgroundPosition = 'center';
      }
    }

    // Sort layers by zIndex and render
    const sortedLayers = [...design.layers].sort((a, b) => a.zIndex - b.zIndex);

    for (const layer of sortedLayers) {
      if (layer.visible === false) continue;
      const element = this.createLayerElement(layer);
      if (element) {
        this.contentContainer.appendChild(element);
      }
    }
  }

  /**
   * Create DOM element for a design layer
   */
  private createLayerElement(layer: DesignLayer): HTMLElement | null {
    const wrapper = document.createElement('div');
    wrapper.className = `layer layer-${layer.type}`;
    wrapper.style.cssText = `
      position: absolute;
      left: ${layer.x}px;
      top: ${layer.y}px;
      width: ${layer.width}px;
      height: ${layer.height}px;
      ${layer.rotation ? `transform: rotate(${layer.rotation}deg);` : ''}
      ${layer.opacity !== undefined ? `opacity: ${layer.opacity};` : ''}
    `;

    switch (layer.type) {
      case 'text':
        wrapper.innerHTML = this.renderTextLayer(layer);
        break;

      case 'image':
        wrapper.appendChild(this.renderImageLayer(layer));
        break;

      case 'shape':
        wrapper.appendChild(this.renderShapeLayer(layer));
        break;

      case 'video':
        wrapper.appendChild(this.renderVideoLayer(layer));
        break;

      case 'widget':
        wrapper.appendChild(this.renderWidgetLayer(layer));
        break;

      default:
        console.warn('Unknown layer type:', (layer as DesignLayer).type);
        return null;
    }

    return wrapper;
  }

  private renderTextLayer(layer: DesignLayer & { type: 'text' }): string {
    // Layer properties come from layer.props (DB schema format) or flat on layer
    const p = (layer as any).props || layer;
    const text = p.text || p.content || '';
    const textAlign = p.textAlign || 'left';
    const verticalAlign = p.verticalAlign || 'top';
    const style = `
      font-family: ${p.fontFamily || 'sans-serif'};
      font-size: ${p.fontSize || 16}px;
      font-weight: ${p.fontWeight || 'normal'};
      font-style: ${p.fontStyle || 'normal'};
      color: ${p.color || '#ffffff'};
      text-align: ${textAlign};
      line-height: ${p.lineHeight || 1.2};
      letter-spacing: ${p.letterSpacing || 0}px;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: ${verticalAlign === 'bottom' ? 'flex-end' : verticalAlign === 'middle' ? 'center' : 'flex-start'};
      justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};
    `;

    return `<div style="${style}">${text}</div>`;
  }

  private renderImageLayer(layer: DesignLayer & { type: 'image' }): HTMLElement {
    const p = (layer as any).props || layer;
    const img = document.createElement('img');
    img.src = p.src || '';
    img.alt = '';
    img.draggable = false;
    img.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: ${p.objectFit || 'contain'};
    `;
    return img;
  }

  private renderShapeLayer(layer: DesignLayer & { type: 'shape' }): HTMLElement {
    const p = (layer as any).props || layer;
    const div = document.createElement('div');
    const fill = p.fill || p.fillColor;
    const stroke = p.stroke || p.strokeColor;
    let style = `
      width: 100%;
      height: 100%;
      ${fill ? `background-color: ${fill};` : ''}
      ${stroke ? `border: ${p.strokeWidth || 1}px solid ${stroke};` : ''}
    `;

    if (p.shapeType === 'circle') {
      style += 'border-radius: 50%;';
    } else if (p.shapeType === 'rectangle' && (p.cornerRadius || p.borderRadius)) {
      style += `border-radius: ${p.cornerRadius || p.borderRadius}px;`;
    }

    div.style.cssText = style;
    return div;
  }

  private renderVideoLayer(layer: DesignLayer & { type: 'video' }): HTMLElement {
    const p = (layer as any).props || layer;
    const video = document.createElement('video');
    video.src = p.src || '';
    video.autoplay = p.autoplay !== false;
    video.loop = p.loop !== false;
    video.muted = p.muted !== false;
    video.playsInline = true;
    video.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: ${p.objectFit || 'cover'};
      ${p.borderRadius ? `border-radius: ${p.borderRadius}px;` : ''}
    `;
    return video;
  }

  private renderWidgetLayer(layer: DesignLayer & { type: 'widget' }): HTMLElement {
    const p = (layer as any).props || layer;
    const container = document.createElement('div');
    container.className = `widget widget-${p.widgetType || layer.type}`;
    container.style.cssText = `
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const config = p.config || layer.config || p;
    let widget: Widget | null = null;

    switch (p.widgetType || layer.widgetType) {
      case 'clock':
        widget = new ClockWidget(config);
        break;

      case 'weather':
        widget = new WeatherWidget(config);
        break;

      case 'qr':
        widget = new QRCodeWidget(config);
        break;

      case 'rss':
        widget = new RSSWidget(config);
        break;

      default:
        console.warn(`[ZoneRenderer] Unknown widget type: ${layer.widgetType}`);
        container.innerHTML = `<div style="opacity: 0.5;">${layer.widgetType} Widget</div>`;
        return container;
    }

    if (widget) {
      const widgetElement = widget.render();
      container.appendChild(widgetElement);
      this.activeWidgets.push(widget); // Track for cleanup
    }

    return container;
  }


  /**
   * Play next item in the playlist
   */
  private async playNextItem(): Promise<void> {
    if (!this.isPlaying || !this.playlist) return;

    const items = this.playlist.items;
    if (items.length === 0) {
      console.warn(`Zone ${this.zone.name}: Playlist is empty`);
      return;
    }

    const item = items[this.currentItemIndex];
    const asset = item.assetId ? this.assets.get(item.assetId) : undefined;

    if (!asset) {
      console.warn(`Zone ${this.zone.name}: Asset not found: ${item.assetId || item.layoutId || 'unknown'}`);
      this.currentItemIndex = (this.currentItemIndex + 1) % items.length;
      if (this.isPlaying) {
        this.playTimer = setTimeout(() => {
          this.playNextItem().catch((e) => {
            if (e?.name !== 'AbortError') console.error(`Zone ${this.zone.name}: Playback error:`, e);
          });
        }, 1000);
      }
      return;
    }

    try {
      await this.renderAsset(asset, item.fitMode || this.zone.fitMode);

      // For videos, wait for completion; for images, wait for duration
      if (asset.type === 'video') {
        await this.waitForVideoCompletion();
      } else {
        // item.duration is in seconds from manifest, convert to milliseconds
        const durationMs = item.duration * 1000;
        console.log(`[ZoneRenderer] Zone "${this.zone.name}" - Image duration: ${item.duration}s (${durationMs}ms), item ${this.currentItemIndex + 1}/${items.length}`);
        await this.wait(durationMs);
      }

      // Move to next item
      this.currentItemIndex = (this.currentItemIndex + 1) % items.length;

      // Continue playing (await to properly propagate errors)
      if (this.isPlaying) {
        await this.playNextItem();
      }
    } catch (error) {
      // Ignore abort errors (normal when stopping playback)
      if ((error as Error)?.name === 'AbortError') {
        console.log(`Zone ${this.zone.name}: Playback aborted`);
        return;
      }

      console.error(`Zone ${this.zone.name}: Error playing item:`, error);
      this.currentItemIndex = (this.currentItemIndex + 1) % items.length;
      if (this.isPlaying) {
        this.playTimer = setTimeout(() => {
          this.playNextItem().catch((e) => {
            if (e?.name !== 'AbortError') console.error(`Zone ${this.zone.name}: Playback error:`, e);
          });
        }, 1000);
      }
    }
  }

  /**
   * Render an asset in this zone
   */
  private async renderAsset(
    asset: ManifestAsset,
    fitMode: 'FIT' | 'FILL'
  ): Promise<void> {
    // Report end of previous asset
    if (this.currentAssetId && this.onPlayEvent && this.playlist) {
      const endEvent: PlayEvent = {
        assetId: this.currentAssetId,
        playlistId: this.playlist.id,
        startedAt: new Date(this.playStartTime).toISOString(),
        endedAt: new Date().toISOString(),
        durationMs: Date.now() - this.playStartTime,
        completed: true,
      };
      this.onPlayEvent(endEvent);
    }

    const objectFit = fitMode === 'FILL' ? 'cover' : 'contain';
    const url = (await manifestLoader.getAssetUrl(asset.id)) || asset.url;

    // Create new element
    let newElement: HTMLElement;

    if (asset.type === 'video') {
      const video = document.createElement('video');
      video.src = url;
      video.autoplay = true;
      video.loop = false;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.style.cssText = `
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: ${objectFit};
      `;
      // Wait for video to be ready before displaying (with timeout fallback)
      await new Promise<void>((resolve) => {
        if (video.readyState >= 3) {
          resolve();
          return;
        }
        const timeout = setTimeout(() => {
          video.removeEventListener('canplay', onReady);
          console.warn(`[ZoneRenderer] Video canplay timeout in zone "${this.zone.name}"`);
          resolve();
        }, 3000);
        const onReady = () => {
          clearTimeout(timeout);
          resolve();
        };
        video.addEventListener('canplay', onReady, { once: true });
      });
      newElement = video;
    } else {
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.draggable = false;
      img.style.cssText = `
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: ${objectFit};
      `;
      // Decode image into GPU memory before displaying to prevent blank first frame
      try {
        await img.decode();
      } catch (e) {
        console.warn(`[ZoneRenderer] img.decode() failed for zone "${this.zone.name}", continuing:`, e);
      }
      newElement = img;
    }

    // Fade transition — new element fades in on top, old stays visible underneath
    newElement.style.opacity = '0';
    newElement.style.zIndex = '1';
    newElement.style.transition = `opacity ${PLAYER_CONFIG.DEFAULT_TRANSITION_DURATION_MS}ms ease-in-out`;
    this.contentContainer.appendChild(newElement);

    // Trigger fade in (keep old element at full opacity to prevent brightness dip)
    requestAnimationFrame(() => {
      newElement.style.opacity = '1';
    });

    // Wait for transition then cleanup
    await this.wait(PLAYER_CONFIG.DEFAULT_TRANSITION_DURATION_MS);

    if (this.currentElement) {
      this.currentElement.remove();
    }

    newElement.style.zIndex = '0';
    this.currentElement = newElement;
    this.currentAssetId = asset.id;
    this.playStartTime = Date.now();
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already aborted
      if (this.abortController?.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      this.playTimer = setTimeout(resolve, ms);

      // Listen for abort signal to cancel the wait
      const abortHandler = () => {
        if (this.playTimer) {
          clearTimeout(this.playTimer);
          this.playTimer = null;
        }
        reject(new DOMException('Aborted', 'AbortError'));
      };

      this.abortController?.signal.addEventListener('abort', abortHandler, { once: true });
    });
  }

  private waitForVideoCompletion(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already aborted
      if (this.abortController?.signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const video = this.currentElement as HTMLVideoElement;
      if (!video || video.ended || video.paused) {
        resolve();
        return;
      }

      const handler = () => {
        video.removeEventListener('ended', handler);
        video.removeEventListener('error', handler);
        this.abortController?.signal.removeEventListener('abort', abortHandler);
        resolve();
      };

      const abortHandler = () => {
        video.removeEventListener('ended', handler);
        video.removeEventListener('error', handler);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      video.addEventListener('ended', handler);
      video.addEventListener('error', handler);
      this.abortController?.signal.addEventListener('abort', abortHandler, { once: true });
    });
  }

  /**
   * Clean up all active widgets
   */
  private destroyWidgets(): void {
    for (const widget of this.activeWidgets) {
      try {
        widget.destroy();
      } catch (error) {
        console.error('[ZoneRenderer] Error destroying widget:', error);
      }
    }
    this.activeWidgets = [];
  }

  /**
   * Stop playback in this zone
   */
  stop(): void {
    this.stopPlayback();
  }

  /**
   * Clean up style elements
   */
  private cleanupStyleElements(): void {
    for (const style of this.styleElements) {
      style.remove();
    }
    this.styleElements = [];
  }

  /**
   * Destroy this zone and clean up
   */
  destroy(): void {
    this.stop();
    this.destroyWidgets();
    this.cleanupStyleElements();

    // Properly clean up video elements before removing container
    const videos = this.container.querySelectorAll('video');
    videos.forEach((video) => {
      video.pause();
      video.src = '';
      video.load();
    });

    this.container.remove();
  }

  /**
   * Get the zone configuration
   */
  getZone(): ManifestZone {
    return this.zone;
  }
}

/**
 * Load custom brand fonts from manifest into the document.
 * Should be called once when a new manifest is loaded, before rendering.
 */
export async function loadCustomFonts(fonts: ManifestFont[] | undefined): Promise<void> {
  if (!fonts?.length) return;

  // Remove old custom font styles
  const existing = document.querySelector('style[data-govista-fonts]');
  if (existing) existing.remove();

  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-govista-fonts', '');

  const rules = fonts.flatMap((font) =>
    font.variants.map((v) => {
      const format = getFormatFromUrl(v.url);
      return `@font-face {
  font-family: '${font.family}';
  src: url('${v.url}') format('${format}');
  font-weight: ${v.weight};
  font-style: ${v.style};
  font-display: swap;
}`;
    })
  );

  styleEl.textContent = rules.join('\n');
  document.head.appendChild(styleEl);

  // Wait for fonts to load
  try {
    await document.fonts.ready;
  } catch {
    // Font loading failed — continue with fallback fonts
  }
}

function getFormatFromUrl(url: string): string {
  if (url.endsWith('.woff2')) return 'woff2';
  if (url.endsWith('.woff')) return 'woff';
  if (url.endsWith('.ttf')) return 'truetype';
  if (url.endsWith('.otf')) return 'opentype';
  return 'woff2';
}
